import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowLeft, FiClock, FiRepeat, FiHeart, FiTarget, FiCalendar, FiUsers, FiZap, FiMessageSquare, FiAward, FiShare2 } = FiIcons;

const AuditScreen = ({ formData, onSubmit, onBack }) => {
  const [answers, setAnswers] = useState({
    fuentes_trafico: null,
    formularios_integrados: null,
    crm_centralizado: null,
    velocidad: null,
    persistencia: null,
    multicanal_automatizado: null,
    agenda_automatica: null,
    scripts_estandarizados: null,
    recordatorios_automaticos: null,
    base_segmentada: null,
    seguimiento_postventa: null,
    referidos_automatizados: null
  });

  const questions = [
    // ETAPA 1 - CAPTURA
    {
      id: 'fuentes_trafico',
      icon: FiTarget,
      title: '🎯 Etapa 1: Captura de Leads',
      subtitle: 'Aquí medimos qué tan bien estás generando y capturando oportunidades nuevas.',
      question: '¿Hoy tienes al menos una fuente activa de generación de leads (anuncios, campañas en redes o tráfico pagado/orgánico constante)?',
      insight: {
        yes: '🟢 ¡Excelente! Tener fuentes activas es el primer paso para crecer',
        no: '🔴 Sin tráfico constante, tu embudo se seca. Cada día sin leads es dinero perdido.'
      },
      scoreImpact: 10,
      stage: 'captura'
    },
    {
      id: 'formularios_integrados',
      icon: FiMessageSquare,
      title: '',
      subtitle: '',
      question: '¿Los clics de tus anuncios o redes llegan a un formulario o chat donde se capturan nombre, teléfono y correo automáticamente?',
      insight: {
        yes: '🟢 Perfecto! Capturar datos completos evita que se pierdan oportunidades',
        no: '🔴 Muchos leads interesados se van si les pides demasiado esfuerzo para contactarte'
      },
      scoreImpact: 10,
      stage: 'captura'
    },
    {
      id: 'crm_centralizado',
      icon: FiUsers,
      title: '',
      subtitle: '',
      question: '¿Todos tus leads se guardan automáticamente en un solo sistema (CRM/app) sin depender de Excel manual?',
      insight: {
        yes: '🟢 Genial! La centralización evita que se pierdan leads entre hojas de cálculo',
        no: '🔴 Sin base central, 30% de tus leads se pierden en el proceso manual'
      },
      scoreImpact: 10,
      stage: 'captura'
    },
    
    // ETAPA 2 - SEGUIMIENTO
    {
      id: 'velocidad',
      icon: FiClock,
      title: '⏱️ Etapa 2: Seguimiento 24/7',
      subtitle: 'Aquí medimos qué tan rápido y persistente eres con tus leads.',
      question: '¿Contactas al lead en los primeros 5 minutos, 24/7?',
      insight: {
        yes: '🟢 Excelente! La velocidad es clave para conversiones',
        no: '🔴 Cada minuto de retraso reduce conversión en 10%'
      },
      scoreImpact: 30,
      stage: 'seguimiento'
    },
    {
      id: 'persistencia',
      icon: FiRepeat,
      title: '',
      subtitle: '',
      question: '¿Realizas entre 5 y 8 intentos de contacto antes de rendirte con un lead?',
      insight: {
        yes: '🟢 Perfecto! La persistencia genera hasta 80% más ventas',
        no: '🔴 El 80% de ventas ocurre después del 5to contacto'
      },
      scoreImpact: 25,
      stage: 'seguimiento'
    },
    {
      id: 'multicanal_automatizado',
      icon: FiZap,
      title: '',
      subtitle: '',
      question: '¿Tu seguimiento usa al menos 2 canales automatizados (WhatsApp + email o WhatsApp + SMS) sin depender de que alguien se acuerde?',
      insight: {
        yes: '🟢 ¡Excelente! El multicanal aumenta respuesta hasta 300%',
        no: '🔴 Depender solo de un canal reduce tu tasa de respuesta en 70%'
      },
      scoreImpact: 10,
      stage: 'seguimiento'
    },

    // ETAPA 3 - CONVERSIÓN
    {
      id: 'agenda_automatica',
      icon: FiCalendar,
      title: '💰 Etapa 3: Conversión a Ventas',
      subtitle: 'Aquí vemos qué tan fácil es que un lead se convierta en cliente.',
      question: '¿Tus prospectos pueden agendar una cita o llamada directamente en un calendario online sin esperar a que alguien les conteste?',
      insight: {
        yes: '🟢 Perfecto! La auto-agenda elimina fricción y duplica tus citas',
        no: '🔴 Cada paso extra para agendar reduce conversiones en 40%'
      },
      scoreImpact: 10,
      stage: 'conversion'
    },
    {
      id: 'scripts_estandarizados',
      icon: FiMessageSquare,
      title: '',
      subtitle: '',
      question: '¿Tu equipo usa un guion o estructura clara para las llamadas y mensajes de venta (no improvisan cada vez)?',
      insight: {
        yes: '🟢 ¡Genial! Los guiones estandarizan el éxito y evitan errores humanos',
        no: '🔴 Sin guiones, cada venta depende del humor/experiencia de la persona'
      },
      scoreImpact: 10,
      stage: 'conversion'
    },
    {
      id: 'recordatorios_automaticos',
      icon: FiClock,
      title: '',
      subtitle: '',
      question: '¿Tienes recordatorios automáticos de cierre (mensajes de "última llamada" o seguimiento a cotizaciones enviadas)?',
      insight: {
        yes: '🟢 ¡Excelente! Los recordatorios automatizados cierran 35% más ventas',
        no: '🔴 La mayoría de los cierres se dan con recordatorios estructurados'
      },
      scoreImpact: 10,
      stage: 'conversion'
    },

    // ETAPA 4 - FIDELIZACIÓN
    {
      id: 'base_segmentada',
      icon: FiUsers,
      title: '♻️ Etapa 4: Fidelización y Recompra',
      subtitle: 'Aquí medimos si exprimes el valor de cada cliente en el tiempo.',
      question: '¿Tienes una base de clientes actualizada y segmentada (por tipo de servicio, fecha de compra o ticket) para poder relanzar campañas específicas?',
      insight: {
        yes: '🟢 ¡Perfecto! La segmentación multiplica el valor de vida del cliente (LTV)',
        no: '🔴 Sin segmentación, pierdes 60% del potencial de recompra de tus clientes'
      },
      scoreImpact: 10,
      stage: 'fidelizacion'
    },
    {
      id: 'seguimiento_postventa',
      icon: FiHeart,
      title: '',
      subtitle: '',
      question: '¿Envías mensajes automáticos después de la compra (agradecimiento, instrucciones, encuestas, upsells)?',
      insight: {
        yes: '🟢 ¡Genial! El post-venta automatizado reduce devoluciones en 25%',
        no: '🔴 Sin seguimiento post-venta, la relación se enfría y pierdes upsells'
      },
      scoreImpact: 10,
      stage: 'fidelizacion'
    },
    {
      id: 'referidos_automatizados',
      icon: FiShare2,
      title: '',
      subtitle: '',
      question: '¿Pides reseñas y referidos en automático a tus clientes satisfechos?',
      insight: {
        yes: '🟢 ¡Excelente! Los referidos automatizados generan 3x más clientes nuevos',
        no: '🔴 Estás desaprovechando el boca-a-boca digital, tu mejor canal de adquisición'
      },
      scoreImpact: 10,
      stage: 'fidelizacion'
    }
  ];

  const handleAnswerChange = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleSubmit = () => {
    const allAnswered = Object.values(answers).every(answer => answer !== null);
    if (allAnswered) {
      onSubmit(answers);
    }
  };

  const allAnswered = Object.values(answers).every(answer => answer !== null);

  // Group questions by stage
  const questionsByStage = {
    captura: questions.filter(q => q.stage === 'captura'),
    seguimiento: questions.filter(q => q.stage === 'seguimiento'),
    conversion: questions.filter(q => q.stage === 'conversion'),
    fidelizacion: questions.filter(q => q.stage === 'fidelizacion')
  };

  return (
    <div className="min-h-screen p-4">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center mb-8">
          <button 
            onClick={onBack}
            className="flex items-center text-orange-400 hover:text-orange-300 transition-colors font-medium"
          >
            <SafeIcon icon={FiArrowLeft} className="text-2xl mr-2" />
            Volver
          </button>
        </div>

        {/* Updated Logo */}
        <div className="text-center mb-8">
          <img 
            src="https://storage.googleapis.com/msgsndr/0yNW04bxSDmn243guLoT/media/6901a9298cdf03a50ab1cf7b.svg" 
            alt="TITAN10X" 
            className="h-24 md:h-32 object-contain filter drop-shadow-xl mx-auto mb-6"
          />
          <h1 className="text-3xl font-bold text-white mb-8">
            🔍 Auditoría Completa del Embudo de Ventas
          </h1>
        </div>

        <div className="bg-gray-800/60 backdrop-blur-md rounded-2xl p-6 mb-8 border border-gray-700 shadow-xl">
          <h2 className="text-2xl font-bold text-white mb-4">📊 Resumen de tu negocio</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-gray-900/50 rounded-xl">
              <p className="text-gray-400 text-sm uppercase tracking-wide">Ticket Promedio</p>
              <p className="text-2xl font-bold text-orange-400">
                ${formData.ticketPromedio.toLocaleString('es-MX')} MXN
              </p>
            </div>
            <div className="text-center p-4 bg-gray-900/50 rounded-xl">
              <p className="text-gray-400 text-sm uppercase tracking-wide">Leads Mensuales</p>
              <p className="text-2xl font-bold text-pink-400">
                {formData.leadsMenuales}
              </p>
            </div>
            <div className="text-center p-4 bg-gray-900/50 rounded-xl">
              <p className="text-gray-400 text-sm uppercase tracking-wide">Ingreso Potencial</p>
              <p className="text-2xl font-bold text-green-400">
                ${(formData.ticketPromedio * formData.leadsMenuales).toLocaleString('es-MX')} MXN
              </p>
            </div>
          </div>
        </div>

        {/* Render questions by stage */}
        {Object.entries(questionsByStage).map(([stage, stageQuestions]) => (
          <div key={stage} className="mb-12">
            {stageQuestions.map((q, index) => (
              <div key={q.id} className="bg-gray-800/60 backdrop-blur-md rounded-2xl p-6 border border-gray-700 hover:border-orange-500/30 transition-colors">
                {/* Stage header (only show for first question of each stage) */}
                {index === 0 && (
                  <div className="mb-6">
                    <h3 className="text-2xl font-bold text-white mb-2">{q.title}</h3>
                    <p className="text-lg text-gray-300">{q.subtitle}</p>
                  </div>
                )}
                
                <div className="flex items-center mb-4">
                  <div className="p-3 bg-gray-700/50 rounded-lg mr-4">
                    <SafeIcon icon={q.icon} className="text-3xl text-orange-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-lg text-gray-300 pl-2 border-l-4 border-gray-700">{q.question}</p>
                  </div>
                </div>
                
                <div className="flex gap-4 mb-4">
                  <button
                    onClick={() => handleAnswerChange(q.id, true)}
                    className={`flex-1 py-3 px-6 rounded-xl font-semibold transition-all ${
                      answers[q.id] === true
                        ? 'bg-green-600 text-white shadow-lg shadow-green-900/20'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    ✅ Sí
                  </button>
                  <button
                    onClick={() => handleAnswerChange(q.id, false)}
                    className={`flex-1 py-3 px-6 rounded-xl font-semibold transition-all ${
                      answers[q.id] === false
                        ? 'bg-red-600 text-white shadow-lg shadow-red-900/20'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    ❌ No
                  </button>
                </div>

                {answers[q.id] !== null && (
                  <div className={`rounded-lg p-4 border ${answers[q.id] ? 'bg-green-900/20 border-green-900/50' : 'bg-red-900/20 border-red-900/50'}`}>
                    <p className={`text-sm ${answers[q.id] ? 'text-green-300' : 'text-red-300'}`}>
                      {answers[q.id] ? q.insight.yes : q.insight.no}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}

        {/* Simple Button - No Score Display */}
        {allAnswered && (
          <div className="text-center pb-12">
            <button
              onClick={handleSubmit}
              className="bg-gradient-to-r from-orange-500 to-pink-600 text-white font-bold py-6 px-16 rounded-2xl text-2xl hover:from-orange-600 hover:to-pink-700 transform hover:scale-[1.02] transition-all duration-200 shadow-xl shadow-orange-500/20"
            >
              📊 Ver mi diagnóstico de pérdidas
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditScreen;